/**
 * Data layer for `bulk-import-lost-orders`. Thin typed fetch wrappers over /api/v1/bulk-import-lost-orders.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface BulkImportLostOrdersRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListBulkImportLostOrdersResponse {
	count: number;
	items: BulkImportLostOrdersRow[];
}

export interface CreateBulkImportLostOrdersBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/bulk-import-lost-orders";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListBulkImportLostOrdersResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListBulkImportLostOrdersResponse>(res);
}

export async function create(body: CreateBulkImportLostOrdersBody): Promise<BulkImportLostOrdersRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<BulkImportLostOrdersRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const bulkImportLostOrdersQueryKeys = {
	all: ["bulk-import-lost-orders"] as const,
	list: () => [...bulkImportLostOrdersQueryKeys.all] as const,
	detail: (id: string) => [...bulkImportLostOrdersQueryKeys.all, id] as const,
};
